import React from 'react';
import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getApiClient } from '../../api';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'primereact/dropdown';
import { redirectErrorPage } from '../../utils/errors';
import ContentSection from '../../components/common/ContentSection/ContentSection';
import DataCards from '../../components/dataCards/DataCards/DataCards';
import { VotingUnityCard } from '../../components/dataCards/dataCardsTemplates/VotingUnityCard';
import type { VotesQuery } from '../../api';
import { URLKeys } from '../../Constants';
import { useCouncilPath, useMunicipalityId } from '../../hooks/useCouncilPath';

const sortByAgreementPercentage = (
    a: { vote_match?: string },
    b: { vote_match?: string },
    sortByCode: 'shoda-sestupne' | 'shoda-vzestupne'
) => {
    const parsePercentage = (value?: string) => parseFloat(value || '0');
    const aValue = parsePercentage(a.vote_match);
    const bValue = parsePercentage(b.vote_match);

    return sortByCode === 'shoda-sestupne' ? bValue - aValue : aValue - bValue;
};

function VotingUnity() {
    const [data, setData] = useState<VotesQuery[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const { bodyType, zastupitelstvoId } = useParams();
    const municipalitaId = useMunicipalityId();
    const councilPath = useCouncilPath();
    const { t } = useTranslation(['analyses', 'translation']);

    const sortByOptions: { name: string; code: 'shoda-sestupne' | 'shoda-vzestupne' }[] = [
        { name: t('cohesion.largestAgreement'), code: 'shoda-sestupne' },
        { name: t('cohesion.lowestAgreement'), code: 'shoda-vzestupne' }
    ];

    const [sortByState, setSortByState] = useState<{
        name: string;
        code: 'shoda-sestupne' | 'shoda-vzestupne';
    }>(sortByOptions.find((option) => option.code === searchParams.get(URLKeys.sortBy)) || sortByOptions[0]);

    useEffect(() => {
        setSearchParams(
            (prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set(URLKeys.sortBy, sortByState.code);
                return newParams;
            },
            { replace: true }
        );
    }, [setSearchParams, sortByState.code]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const api = getApiClient();
                const votesData = await api.getVotesByMunicipalityIdAndBodyAndCouncilId(
                    municipalitaId ?? '',
                    bodyType ?? '',
                    parseInt(zastupitelstvoId ?? '0', 10),
                    { pohled: 'shoda' }
                );
                setData(votesData ?? []);
            } catch (error) {
                redirectErrorPage(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [bodyType, municipalitaId, zastupitelstvoId]);

    return (
        <>
            <ContentSection title={t('cohesion.voting')} level={1} subtitle={t('cohesion.votingTooltip')} className='page-title-section' />
            <ContentSection level={3} contentClassName='filtering'>
                <span className='p-float-label'>
                    <Dropdown
                        value={sortByState}
                        options={sortByOptions}
                        onChange={(e) => setSortByState(e.value)}
                        optionLabel='name'
                        inputId='sortBy'
                    />
                    <label htmlFor='sortBy'>{t('sortBy', {ns : 'translation'})}</label>
                </span>
            </ContentSection>
            <ContentSection level={3} className='dashboard-card'>
                <DataCards
                    renderCardCallback={(cardData: VotesQuery) => <VotingUnityCard data={cardData} />}
                    getCardUrl={(item) => councilPath('hlasovani', item.id_hlasovani)}
                    getUniqueId={(item) => item.id_hlasovani}
                    cardsData={(() => {
                        return data.sort((a: VotesQuery, b: VotesQuery) => sortByAgreementPercentage(a, b, sortByState.code));
                    })()}
                    totalResultText={t('cohesion.votingsCountTotal', {
                        totalCount: data.length
                    })}
                    loading={loading}
                />
            </ContentSection>
        </>
    );
}

export default VotingUnity;
